import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Order } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { Package, ShieldCheck, Zap, ArrowRight, ExternalLink } from 'lucide-react';
import { WarrantyBadge } from '../../components/common/Badge';

export const BuyerOrders: React.FC = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await fetch('/api/orders?as=buyer');
        if (res.ok) {
          const data = await res.json();
          if (data.success) setOrders(data.orders || []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      
      <div className="border-b border-brand-lightBorder dark:border-brand-darkBorder pb-6">
        <h1 className="text-3xl font-black text-brand-lightText dark:text-brand-darkText">
          My Purchases & Orders
        </h1>
        <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText mt-1">
          Track active deliveries, view encrypted credentials, and manage warranty claims
        </p>
      </div>

      {orders.length > 0 ? (
        <div className="space-y-4">
          {orders.map((order) => (
            <div
              key={order.id}
              className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder shadow-sm hover:border-brand-blue/40 transition-all flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
            >
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-14 h-14 rounded-2xl bg-brand-lightSec dark:bg-brand-darkElevated flex items-center justify-center flex-shrink-0">
                  {order.game_icon ? (
                    <img src={order.game_icon} alt="" className="w-10 h-10 rounded-xl object-cover" />
                  ) : (
                    <Package className="w-6 h-6 text-brand-blue" />
                  )}
                </div>

                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold text-brand-lightSecText">
                      #{order.order_number}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                      order.status === 'completed'
                        ? 'bg-emerald-500/10 text-emerald-600'
                        : order.status === 'delivered'
                        ? 'bg-blue-500/10 text-brand-blue'
                        : 'bg-amber-500/10 text-amber-600'
                    }`}>
                      {order.status}
                    </span>
                  </div>

                  <h3 className="font-bold text-sm text-brand-lightText dark:text-brand-darkText truncate mt-0.5">
                    {order.listing_title || 'Game Digital Order'}
                  </h3>

                  <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText mt-0.5">
                    Seller: <strong>{order.seller_store_name || 'Verified Vendor'}</strong> • Date: {new Date(order.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between md:justify-end gap-6 w-full md:w-auto border-t md:border-t-0 pt-4 md:pt-0 border-brand-lightBorder dark:border-brand-darkBorder">
                <div className="text-left md:text-right">
                  <span className="font-black text-lg text-brand-blue dark:text-brand-accent">
                    ${(order.total_cents / 100).toFixed(2)} USD
                  </span>
                  <span className="text-[10px] text-emerald-600 block font-semibold">
                    +{order.points_rewarded} pts
                  </span>
                </div>

                <Link
                  to={`/checkout/status/${order.id}`}
                  className="px-4 py-2 rounded-xl font-bold text-xs text-white bg-brand-blue hover:bg-brand-hover shadow-glow-blue flex items-center gap-1.5 transition-all"
                >
                  <span>Order Details</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-16 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder text-center space-y-3">
          <Package className="w-12 h-12 text-brand-lightSecText mx-auto" />
          <h3 className="font-bold text-base text-brand-lightText dark:text-brand-darkText">No orders placed yet</h3>
          <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText">Explore our verified marketplace catalog to find high-tier accounts and games.</p>
          <Link
            to="/browse"
            className="inline-block mt-2 px-6 py-2.5 rounded-xl font-bold text-xs text-white bg-brand-blue"
          >
            Browse Offers
          </Link>
        </div>
      )}
    </div>
  );
};
